import { useCallback, useMemo, useEffect, useContext } from 'react'
import { CustomerDetailFragment } from 'src/api/api.generated'
import { ReservationF } from '../types/reservation-detail.types'
import { GuestOccupancyCheckInWarning } from '../tabs/reserved-resources-tab/check-in/check.in.types'
import { GuestCheckinCheckoutAction, GuestOccupancy } from '../types/guests.types'
import { ReservationContext } from './reservation-context'
import { Context, RRContext } from './reservation-context.types'

export type ReservationContextConsumerChildrenParams = {
  reservation: ReservationF
  context: Context
  reservedResourceContext: RRContext
  guestOccupanciesWarnings: GuestOccupancyCheckInWarning[]
  submitReservedResource: () => Promise<void>
  submitReduceGuestCount: (index: number) => Promise<void>
  submitAddGuest: (childId: string | null, isReplaceOfCheckoutGuest?: boolean) => Promise<void>
  guestOccupanciesErrorShown: () => void
  onGuestChange: (guestIndex: number, guest: GuestOccupancy) => void
  onCustomerUpserted: (guestIndex: number, customer: CustomerDetailFragment) => void
  customerErrorShown: (guestIndex: number) => void
  cancelChanges: () => void
  performGuestCheckinCheckout: (guestId: string, action: GuestCheckinCheckoutAction) => void
  disableAddGuests?: boolean
}

export type ReservationContextConsumerProps = {
  reservedResourceId: string
  children: (params: ReservationContextConsumerChildrenParams) => React.ReactNode
  disableAddGuests?: boolean
  unsavedChanges: (rrId: string | undefined) => void
}

export function ReservationContextConsumer({ reservedResourceId, children, disableAddGuests, unsavedChanges }: ReservationContextConsumerProps) {
  const context = useContext(ReservationContext)
  return <Currier {...{ context, reservedResourceId, children, disableAddGuests }} unsavedChanges={unsavedChanges} />
}

type CurrierProps = {
  context: Context
  reservedResourceId: string
  children: (params: ReservationContextConsumerChildrenParams) => React.ReactNode
  disableAddGuests?: boolean
  unsavedChanges: (rrId: string | undefined) => void
}
function Currier({ context, reservedResourceId, children, disableAddGuests, unsavedChanges }: CurrierProps) {
  useEffect(() => {
    console.log('Currier Effect on reservation', context.reservation)
  }, [context.reservation])

  const rrContext = useMemo(() => context.reservedResources.get(reservedResourceId), [JSON.stringify(context.reservedResources.get(reservedResourceId))])

  useEffect(() => {
    unsavedChanges(rrContext?.hasChanged ? reservedResourceId : undefined)
  }, [rrContext])

  const guestOccupanciesWarnings = useMemo(
    () => context.checkInWarning.reservedResources?.find((rr) => rr.reservedResourceId === reservedResourceId)?.guestOccupancyWarnings ?? [],
    [context.checkInWarning],
  )
  const submitReservedResource = useCallback(async () => context.submitReservedResource(reservedResourceId), [reservedResourceId, context.submitReservedResource])
  const submitReduceGuestCount = useCallback(async (index: number) => context.submitReduceGuestCount(reservedResourceId, index), [reservedResourceId, context.submitReduceGuestCount])
  const submitAddGuest = useCallback(
    async (childId: string | null, isReplaceOfCheckoutGuest?: boolean) => context.submitAddGuest(reservedResourceId, childId, isReplaceOfCheckoutGuest),
    [reservedResourceId, context.submitAddGuest],
  )
  const guestOccupanciesErrorShown = useCallback(() => context.guestOccupanciesErrorShown(reservedResourceId), [reservedResourceId, context.guestOccupanciesErrorShown])
  const onGuestChange = useCallback((guestIndex: number, guest: GuestOccupancy) => context.onGuestChange(reservedResourceId, guestIndex, guest), [reservedResourceId, context.onGuestChange])
  const onCustomerUpserted = useCallback(
    (guestIndex: number, customer: CustomerDetailFragment) => context.onCustomerUpserted(reservedResourceId, guestIndex, customer),
    [reservedResourceId, context.onCustomerUpserted],
  )
  const customerErrorShown = useCallback((guestIndex: number) => context.customerErrorShown(reservedResourceId, guestIndex), [reservedResourceId, context.customerErrorShown])
  const cancelChanges = useCallback(() => context.cancelChanges(reservedResourceId), [reservedResourceId, context.cancelChanges])

  const performGuestCheckinCheckout = useCallback(
    (guestId: string, action: GuestCheckinCheckoutAction) => context.performGuestCheckinCheckout(reservedResourceId, guestId, action),
    [reservedResourceId, context.performGuestCheckinCheckout],
  )

  return (
    <>
      {rrContext !== undefined &&
        children({
          reservation: context.reservation,
          context,
          reservedResourceContext: rrContext,
          guestOccupanciesWarnings,
          submitReservedResource,
          submitReduceGuestCount,
          submitAddGuest,
          guestOccupanciesErrorShown,
          onGuestChange,
          onCustomerUpserted,
          customerErrorShown,
          cancelChanges,
          performGuestCheckinCheckout,
          disableAddGuests,
        })}
    </>
  )
}
